import Grid from '@mui/material/Grid';
import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { styled } from '@mui/material/styles';
import { Tooltip, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import SendIcon from '@mui/icons-material/Send';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import LoadingButton from '@mui/lab/LoadingButton';
import { PlantingLocation } from '../Types';
import Divider from '@mui/material/Divider';
import SafeteySlider from './ProbabilitySlider';
import { ConfigContext, FrostDatesContext, PlantingLocationContext, plannerUrls } from '../AppContext';

const TransparentGrid = styled(Grid)(({ theme }) => ({
    ...theme.typography.body2,
    padding: theme.spacing(2),
    paddingTop: 0,
    textAlign: 'center',
    color: 'white',
    backgroundColor: 'rgba(0, 0, 0, 0.8)'
}));
const ZipInput = styled(TextField)(({ theme }) => ({
    textAlign: 'center',
    '&.MuiTextField-root': {
        '& .MuiInputLabel-root': {
            color: 'white',
        },
        '& .MuiOutlinedInput-root': {
            /* focused */
            '&.Mui-focused': {
                '& .MuiOutlinedInput-input': {
                    '&:hover': {
                        border: 'none',
                    }
                },
            },
            '& .MuiOutlinedInput-input': {
                color: 'white',
                textAlign: 'center',
                fontSize: '1.25rem',
                '&:-webkit-autofill': {
                    fontSize: '1.25em',
                    WebkitTextFillColor: 'white',
                    WebkitBackgroundClip: 'text',
                    WebkitBoxShadow: 'none',
                },
                '&:hover': {
                    border: '2px solid white',
                },
                '&.Mui-disabled': {
                    textFillColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                        border: 'none',
                    }
                }
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.85)',
            }
        },
        // }
    },
}));
const GridBgImage = styled(Grid)(({ theme }) => {
    // const { frame: { parentOrigin } } = useContext(FrameContext);
    // console.log('parentOrigin:', parentOrigin);
    return {
        ...theme.typography.body2,
        padding: theme.spacing(0),
        textAlign: 'center',
        color: 'white',
        // backgroundImage: `url(${parentOrigin}/img/planting.png)`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        minHeight: '250px',
        backgroundColor: theme.palette.primary.main,
    }
});
let firstLoad = true;
export default function PlantingPlannerForm() {
    // const { config: {  } } = useContext(ConfigContext);
    const [{ zipCode }, setZipCode] = useState({ zipCode: '' });
    const [{ zipIsValid }, setZipIsValid] = useState({ zipIsValid: false });
    const [{ loadingPlantingLocation }, setLoadingPlantingLocation] = useState({ loadingPlantingLocation: false });
    const { plantingLocation, setPlantingLocation } = useContext(PlantingLocationContext);
    const [{ error }, setError] = useState<{ error: string | null }>({ error: null });
    const { setFrostDates } = useContext(FrostDatesContext);


    function onReset() {
        setZipCode({ zipCode: '' });
        setPlantingLocation(null);
        setZipIsValid({ zipIsValid: false });
        setError({ error: null });
        setFrostDates(null);
    }
    function validateZip() {
        const validateZipRegex = /^\d{5}$/;
        if (validateZipRegex.test(zipCode)) {
            setZipIsValid({ zipIsValid: true });
            setError({ error: null });
        } else {
            setZipIsValid({ zipIsValid: false });
            setError({ error: "Please enter a valid 5-digit zip code." });
        }
    }
    function validateOnSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        validateZip();
    }

    useEffect(() => {
        if (zipIsValid && !plantingLocation) getPlantingLocation();
    }, [zipCode, zipIsValid]);
    // if (initialZipCode && firstLoad) {
    //     firstLoad = false;
    //     validateZip();
    // }
    async function getPlantingLocation() {
        try {
            setLoadingPlantingLocation({ loadingPlantingLocation: true });
            const response = await fetch(`${plannerUrls.api()}/api/location/zipCode?zipCode=${zipCode}`);
            if (!response.ok) {
                setLoadingPlantingLocation({ loadingPlantingLocation: false });
                setError({ error: "We encountered a problem. Please try again later." });
            }
            const data: PlantingLocation = await response.json();
            setPlantingLocation(data);
            setLoadingPlantingLocation({ loadingPlantingLocation: false });
        } catch (error) {
            setError(error.message);
            setLoadingPlantingLocation({ loadingPlantingLocation: false });
        }
    };

    console.log('image used:', `${plannerUrls.bannerImage()}`);
    return (
        <GridBgImage sx={{
            // backgroundImage: `url(/cdn/shop/files/Web_Banner_Header_1800_x_600_px_1600x.png?v=1696279190)`
            // backgroundColor: grey[700],
            justifyContent: 'center',
            alignItems: 'center',
            backgroundImage: `url(${plannerUrls.bannerImage()})`
        }} id="plantingPlanner-bgImg" container>
            <TransparentGrid item md={12} lg={4}>
                <Grid container component="form" onSubmit={validateOnSubmit} onReset={onReset}>
                    <Grid item xs={12}>
                        <Typography variant="h3" component="h1" gutterBottom>
                            Seed Planting Tool
                        </Typography>
                    </Grid>
                    <Grid item sx={{
                        minHeight: '100px'
                    }} xs={6}>
                        <ZipInput
                            disabled={plantingLocation !== null || loadingPlantingLocation}
                            error={error != null}
                            sx={{ margin: 1 }}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setZipCode({ zipCode: event.target.value });
                            }}
                            value={zipCode}
                            label="Zip Code:"
                            helperText={error != null ? error : ""}
                            variant="outlined" />
                    </Grid>
                    <Grid item sx={{ minHeight: '100px' }} xs={6}>
                        {(() => {
                            if (!plantingLocation) {
                                return <Typography padding={2} sx={{ paddingTop: 0 }} variant="h6" component="h2" gutterBottom>
                                    Enter Your Zip Code to Get Started!
                                </Typography>
                            } else {
                                return <ZipInput
                                    disabled
                                    sx={{ margin: 1 }}
                                    InputProps={{
                                        endAdornment: <Tooltip
                                            color='white'
                                            title={`The minimum temperatures you are likely to see in this zone fall between ${plantingLocation?.hardinessZone.minTemp}°F and ${plantingLocation?.hardinessZone.maxTemp}°F`}
                                        >
                                            <InfoIcon sx={{
                                                textFillColor: 'rgba(255, 255, 255, 0.8)',
                                                color: 'white'
                                            }} />
                                        </Tooltip>
                                    }}
                                    value={plantingLocation?.hardinessZone.zone}
                                    label="Your Hardiness Zone:"
                                    helperText={error != null ? error : ""}
                                    variant="outlined" />
                            }
                        })()}
                    </Grid>
                    <Grid item sx={{ minHeight: '150px', display: 'flex', flexDirection: 'column', justifyItems: 'flex-end' }} xs={12}>
                        <SafeteySlider />
                        <Divider sx={{ borderColor: 'rgba(255, 255, 255, .75)', marginTop: 'auto' }} />
                    </Grid>
                    <Grid item pt={2} xs={12}>
                        {(() => {
                            if (!plantingLocation) {
                                return <LoadingButton
                                    type='submit'
                                    sx={{
                                        color: 'white',
                                        borderColor: 'white',
                                        '&.MuiLoadingButton-loading.Mui-disabled': {
                                            color: 'rgb(255, 255, 255, .8)',
                                            borderColor: 'rgb(255, 255, 255, .8)'
                                        }
                                    }}
                                    size="medium"
                                    // onClick={validateOnSubmit}
                                    endIcon={<SendIcon />}
                                    loading={loadingPlantingLocation}
                                    loadingPosition="end"
                                    variant="outlined"
                                >
                                    {(() => loadingPlantingLocation ? <span>Loading...</span> : <span>Submit</span>)()}
                                </LoadingButton>
                            } else {
                                return <LoadingButton
                                    type='reset'
                                    hidden={plantingLocation !== null}
                                    sx={{
                                        color: 'white',
                                        borderColor: 'white'
                                    }}
                                    size="medium"
                                    // onClick={onReset}
                                    endIcon={<ClearIcon />}
                                    loadingPosition="end"
                                    variant="outlined"
                                >
                                    Reset
                                </LoadingButton>
                            }
                        })()}
                    </Grid>
                </Grid>
            </TransparentGrid>
        </GridBgImage>
    )
}
